import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SessionProvider, useSession } from '@/auth/session'
import { visibleNav } from '@/auth/nav'
import { SignIn, PinGate } from '@/pages/SignIn'
import { FirstSetup } from '@/pages/FirstSetup'
import { SecurityPage } from '@/pages/Security'
import { Guarded } from '@/components/Guarded'
import { Button, Notice } from '@/components/ui'
import { ADMIN_STATUS_LABEL, SUPER_ADMIN_LABEL } from '@/api/types'
import { logout } from '@/api/admin'
import { ApiError } from '@/api/errors'
import { onFlash, takeFlash } from '@/auth/pendingWrite'
import { reportAuthFailure } from '@/auth/authEvents'
import { UsersPage, UserDetailPage } from '@/pages/Members'
import { PartnerApplicationsPage } from '@/pages/Approvals'
import { KycVerificationDetailPage, KycVerificationPage } from '@/pages/KycVerification'
import { CollateralPage, PartnerDetailPage } from '@/pages/Collateral'
import { RevocationsPage, RevocationDetailPage } from '@/pages/Revocations'
import { AuditLogsPage } from '@/pages/AuditLogs'
import { IncidentsPage, IncidentDetailPage } from '@/pages/Incidents'
import { TradesPage } from '@/pages/Trades'
import { StaffPage } from '@/pages/Staff'
import { PermissionNamesPage } from '@/pages/PermissionNames'
import { RiskPage, RiskQueueItemPage } from '@/pages/Risk'
import { SettingsPage } from '@/pages/Settings'
import { RecordsPage } from '@/pages/Records'

const qc = new QueryClient({
  // ⚠ **모든 조회의 실패가 여기를 지난다.** 인증이 무너진 것만 골라 세션에
  // 넘기고, 나머지는 화면이 QueryState 로 그린다.
  queryCache: new QueryCache({
    onError: (err) => {
      if (err instanceof ApiError
        && (err.kind === 'session_expired' || err.kind === 'pin_required' || err.kind === 'setup_required')) {
        reportAuthFailure(err.kind)
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: false,           // 401·403 을 다시 두드려 봐야 답이 안 바뀐다
      staleTime: 10_000,
    },
  },
})

/**
 * 쓰기가 끝났다는 한 줄.
 *
 * ⚠ **화면 밖에서 온다.** PIN 때문에 멈췄던 쓰기는 키패드 화면에서 끝나는데,
 * 그때 그 버튼이 있던 화면은 이미 사라진 뒤다 — 결과를 말할 자리가 여기뿐이다.
 */
function Flash() {
  const [msg, setMsg] = useState(() => takeFlash())
  useEffect(() => onFlash(() => setMsg(takeFlash())), [])
  if (!msg) return null
  return (
    <div className="mb-4">
      <Notice tone={msg.tone}>
        <span className="flex items-start gap-3">
          <span className="flex-1">{msg.text}</span>
          <button type="button" onClick={() => setMsg(null)}
            className="text-xs underline">Close</button>
        </span>
      </Notice>
    </div>
  )
}

/**
 * 공통 셸(EA-05).
 *
 * 순서(명세 2-1): 세션 확인 중 → 로그인(EA-01·EA-02) → 처음 설정(EA-03) → PIN(EA-04) →
 * 셸. 메뉴는 XM-06 의 `superAdmin`·`keys` 로 그린다(명세 0-5).
 */
function Shell() {
  const { me, loading, signOut, inactive, pinNeeded, setupNeeded } = useSession()
  const loc = useLocation()

  if (loading) return <p className="p-8 text-sm text-slate-500">Loading…</p>
  if (!me) return <SignIn />

  // ⚠ **처음 설정은 주소와 상관없이 먼저다.** 첫 설정 전 세션은 XM-03·XM-05·XM-06
  // 말고는 403 `setup_required` 다 — 셸을 그려 봐야 모든 화면이 거절된다.
  if (setupNeeded) return <FirstSetup />

  // ⚠ **PIN 은 로그인 다음 단계가 아니라 상시 관문이다.** 서버가 어느 요청에서든
  // 403 `pin_required` 를 줄 수 있다. 여기서 막아야 「메뉴는 보이는데 아무것도 못
  // 여는」 상태에 갇히지 않는다.
  if (pinNeeded) return <PinGate />

  const nav = visibleNav({ superAdmin: me.superAdmin, keys: me.keys })

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <header className="flex items-center gap-6 border-b border-slate-200 bg-white px-6 py-3 dark:border-slate-700 dark:bg-slate-900">
        <span className="whitespace-nowrap font-semibold">SJO Exchange Admin</span>
        {/* 영어 메뉴 이름이 길어 낱말이 갈라지지 않게 — 한 줄에 안 들어가면 메뉴째 다음 줄로(2026-10-02 영어로만). */}
        <nav className="flex flex-wrap gap-1 text-sm">
          {nav.map((n) => (
            <NavLink key={n.path} to={n.path}
              className={({ isActive }) =>
                `whitespace-nowrap rounded px-3 py-1.5 ${isActive ? 'bg-teal-800 text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex shrink-0 items-center gap-3 text-sm">
          {/*
            ⚠ **보안 화면은 좌측 메뉴가 아니라 여기 있다.** 업무 구역이 아니라
            「내 계정」이고, 권한이 아니라 **관리자 세션**으로 들어간다 — 새 권한을
            만들지 않기 위해서이기도 하다(사장님 지시 7).
          */}
          <NavLink to="/account/security"
            className={({ isActive }) =>
              `whitespace-nowrap rounded px-2 py-1 ${isActive ? 'bg-slate-200 dark:bg-slate-700' : 'hover:underline'}`}>
            {me.loginId}
            {me.superAdmin
              ? ` · ${SUPER_ADMIN_LABEL}`
              : me.permissionName && ` · ${me.permissionName.name}`}
            {me.status !== 'ACTIVE' && (
              <span className="ml-2 rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">
                {ADMIN_STATUS_LABEL[me.status]}
              </span>
            )}
          </NavLink>
          {/*
            ⚠ **서버 응답을 기다린 뒤에 나간다.** 백엔드의 로그아웃은 그 계정의
            세션을 **전부** 끊는다(STEP 5 의 뜻이다 — 「로그아웃이 로그아웃이 아닌」
            것을 고치면서 그렇게 됐다). 기다리지 않고 화면만 먼저 나가면, 뒤늦게
            도착한 그 요청이 **그 사이에 새로 로그인한 세션까지 끊는다.**
            브라우저 시험에서 실제로 그렇게 걸렸다.

            실패해도 화면은 나간다 — 손에 든 토큰은 어차피 버린다.
          */}
          <Button tone="plain" onClick={() => {
            void (async () => {
              try { await logout() } catch { /* 나가는 것은 막지 않는다 */ }
              signOut()
            })()
          }}>Sign out</Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl p-6">
        <Flash />
        {inactive && (
          <div className="mb-4">
            <Notice tone="stop">
              <strong>This admin account is not active.</strong> All viewing and actions are refused.
            </Notice>
          </div>
        )}
        <Routes>
          <Route path="/" element={<Navigate to={nav[0]?.path ?? '/users'} replace />} />
          <Route path="/users" element={<Guarded path="/users"><UsersPage /></Guarded>} />
          {/* 회원 주소는 번호다(명세 0-6 고침 — 레거시는 /users/{아이디}). */}
          <Route path="/users/:memberId" element={<Guarded path="/users"><UserDetailPage /></Guarded>} />
          {/* 2026-10-12 (물음 16 ⑤): KYC 인증 — 옛 가입 승인(/signups)을 바꿈. 옛 주소는 여기로. */}
          <Route path="/kyc-verification" element={<Guarded path="/kyc-verification"><KycVerificationPage /></Guarded>} />
          <Route path="/kyc-verification/:applicationId" element={<Guarded path="/kyc-verification"><KycVerificationDetailPage /></Guarded>} />
          <Route path="/signups" element={<Navigate to="/kyc-verification" replace />} />
          <Route path="/partner-applications" element={<Guarded path="/partner-applications"><PartnerApplicationsPage /></Guarded>} />
          <Route path="/collateral" element={<Guarded path="/collateral"><CollateralPage /></Guarded>} />
          <Route path="/collateral/partners/:partnerId" element={<Guarded path="/collateral"><PartnerDetailPage /></Guarded>} />
          <Route path="/master-revocations" element={<Guarded path="/master-revocations"><RevocationsPage /></Guarded>} />
          <Route path="/master-revocations/:id" element={<Guarded path="/master-revocations"><RevocationDetailPage /></Guarded>} />
          <Route path="/audit-logs" element={<Guarded path="/audit-logs"><AuditLogsPage /></Guarded>} />
          <Route path="/risk" element={<Guarded path="/risk"><RiskPage /></Guarded>} />
          <Route path="/risk/queue/:id" element={<Guarded path="/risk"><RiskQueueItemPage /></Guarded>} />
          <Route path="/incidents" element={<Guarded path="/incidents"><IncidentsPage /></Guarded>} />
          <Route path="/incidents/:id" element={<Guarded path="/incidents"><IncidentDetailPage /></Guarded>} />
          <Route path="/trades" element={<Guarded path="/trades"><TradesPage /></Guarded>} />
          <Route path="/staff" element={<Guarded path="/staff"><StaffPage /></Guarded>} />
          <Route path="/settings" element={<Guarded path="/settings"><SettingsPage /></Guarded>} />
          <Route path="/records" element={<Guarded path="/records"><RecordsPage /></Guarded>} />
          <Route path="/permission-names" element={<Guarded path="/permission-names"><PermissionNamesPage /></Guarded>} />

          {/*
            ⚠ **Guarded 를 지나되 권한 조건이 없다.** `mayEnter` 는 NAV 에 없는
            경로를 막지 않으므로, 여기서 걸리는 것은 계정 비활성과 PIN 뿐이다 —
            그것이 「관리자 세션 경계를 쓴다」의 뜻이다.
          */}
          <Route path="/account/security" element={<Guarded path="/account/security"><SecurityPage /></Guarded>} />
          {/* 없는 화면(EA-40). 레거시 pending 자리 /admins 도 여기로 온다. */}
          <Route path="*" element={<Notice tone="warn">This screen does not exist. ({loc.pathname})</Notice>} />
        </Routes>
      </main>
    </div>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <SessionProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </SessionProvider>
    </QueryClientProvider>
  )
}
